import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "../Modal";
import { toast } from "react-toastify";
import {
  sendDownloadOtpAction,
  verifyDownloadOtpAction,
  resendDownloadOtpAction,
} from "../../actions/VerifyOtpAction";
import "./index.scss";

const OTP_LENGTH = 6;
const RESEND_TIMEOUT_SEC = 15 * 60; // 15 minutes

/**
 * DownloadOtpModal
 *
 * Props:
 *  - isOpen       : boolean
 *  - onClose      : () => void
 *  - email        : string  — logged-in user's email (OTP sent here)
 *  - onVerified   : () => Promise<void>  — called after OTP verified; do the download inside
 */
const DownloadOtpModal = ({ isOpen, onClose, email, onVerified }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [sending, setSending] = useState(false); // sending initial OTP
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0); // seconds remaining
  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  // ── Reset when modal opens ──────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setOtp(["", "", "", "", "", ""]);
      setOtpSent(false);
      setResendTimer(0);
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isOpen]);

  // ── Countdown timer ─────────────────────────────────────────────────────────
  const startTimer = useCallback(() => {
    setResendTimer(RESEND_TIMEOUT_SEC);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  }, []);

  const formatTimer = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  // ── Send OTP ────────────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    if (!email) {
      toast.error("No email found for logged-in user");
      return;
    }
    setSending(true);
    const result = await sendDownloadOtpAction(email);
    setSending(false);
    if (result.success) {
      setOtpSent(true);
      startTimer();
      toast.success(result.message || `OTP sent to ${email}`);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } else {
      toast.error(result.message || "Failed to send OTP");
    }
  };

  // ── Resend OTP ──────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendTimer > 0) return;
    setResending(true);
    setOtp(["", "", "", "", "", ""]);
    const result = await resendDownloadOtpAction(email);
    setResending(false);
    if (result.success) {
      startTimer();
      toast.success(result.message || "OTP resent successfully");
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } else {
      toast.error(result.message || "Failed to resend OTP");
    }
  };

  // ── OTP input handlers ──────────────────────────────────────────────────────
  const handleOtpChange = (idx, value) => {
    if (!/^\d*$/.test(value)) return; // digits only
    const next = [...otp];
    next[idx] = value.slice(-1); // max 1 digit per box
    setOtp(next);
    if (value && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) inputRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1)
      inputRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = [...otp];
    pasted.split("").forEach((ch, i) => {
      next[i] = ch;
    });
    setOtp(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  // ── Verify OTP ──────────────────────────────────────────────────────────────
  const enteredOtp = otp.join("");
  const isOtpComplete = enteredOtp.length === OTP_LENGTH;

  const handleVerify = async () => {
    if (!isOtpComplete) {
      toast.error("Please enter the full 6-digit OTP");
      return;
    }
    setVerifying(true);
    const result = await verifyDownloadOtpAction(email, enteredOtp);
    setVerifying(false);
    if (result.success) {
      toast.success(result.message || "OTP verified! Starting download…");
      onClose();
      await onVerified();
    } else {
      toast.error(result.message || "Invalid OTP. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Verify Download"
      size="small"
    >
      <div className="dotp">
        {/* Email info */}
        <div className="dotp__info">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <div>
            <p className="dotp__info-label">OTP will be sent to</p>
            <p className="dotp__info-email">{email || "—"}</p>
          </div>
        </div>

        {!otpSent ? (
          /* ── Step 1: Send OTP ─────────────────────────────────────────── */
          <div className="dotp__step">
            <p className="dotp__hint">
              Click below to receive a one-time password on your email. The OTP
              is valid for <strong>15 minutes</strong>.
            </p>
            <button
              className="dotp__btn dotp__btn--primary"
              onClick={handleSendOtp}
              disabled={sending}
            >
              {sending ? (
                <>
                  <span className="dotp__spinner" /> Sending OTP…
                </>
              ) : (
                "Send OTP"
              )}
            </button>
          </div>
        ) : (
          /* ── Step 2: Enter & Verify OTP ──────────────────────────────── */
          <div className="dotp__step">
            <p className="dotp__hint">
              Enter the 6-digit OTP sent to <strong>{email}</strong>.
            </p>

            {/* OTP boxes */}
            <div className="dotp__boxes" onPaste={handleOtpPaste}>
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  className={`dotp__box${digit ? " dotp__box--filled" : ""}`}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  aria-label={`OTP digit ${idx + 1}`}
                  autoComplete="off"
                />
              ))}
            </div>

            {/* Resend row */}
            <div className="dotp__resend-row">
              {resendTimer > 0 ? (
                <p className="dotp__timer">
                  Resend OTP in <strong>{formatTimer(resendTimer)}</strong>
                </p>
              ) : (
                <button
                  className="dotp__resend-btn"
                  onClick={handleResend}
                  disabled={resending}
                >
                  {resending ? "Resending…" : "Resend OTP"}
                </button>
              )}
            </div>

            {/* Footer actions */}
            <div className="dotp__actions">
              <button
                className="dotp__btn dotp__btn--cancel"
                onClick={onClose}
                disabled={verifying}
              >
                Cancel
              </button>
              <button
                className="dotp__btn dotp__btn--primary"
                onClick={handleVerify}
                disabled={!isOtpComplete || verifying}
              >
                {verifying ? (
                  <>
                    <span className="dotp__spinner" /> Verifying…
                  </>
                ) : (
                  "Verify & Download"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DownloadOtpModal;
